import PropTypes from 'prop-types'
import styled from 'styled-components'
import { useMutation, useQueryClient } from 'react-query'
import { jt, t } from 'ttag'

import { api } from '@h2/api'
import { Modal } from '@h2/components/containers/modal'
import { Flex } from '@h2/components/layout/flex'
import { Box } from '@h2/components/layout/box'
import { Heading } from '@h2/components/typography/heading'
import { Text } from '@h2/components/typography/text'
import { Button } from '@h2/components/buttons/button'
import { QuestionCircleIcon } from '@h2/components/icons/questionCircle'
import { features } from '@h2/components/media'

import { useNavigationHandler } from '../../../hooks/useNavigationHandler'

const Header = styled.header`
  display: flex;
  align-items: center;
`
const CancelButton = styled(Button)`
  display: none;

  @media (${features.minWidth.laptop}) {
    display: inline-flex;
  }
`
const ButtonBox = styled(Box)`
  button {
    width: 100%;
    justify-content: center;

    &:first-child {
      margin: 0 0 15px 0;
    }
  }

  @media (${features.minWidth.laptop}) {
    button {
      width: auto;

      &:first-child {
        margin: 0 15px 0 0;
      }
    }
  }
`
const FlexFooter = styled(Flex)`
  @media (${features.minWidth.laptop}) {
    flex-direction: row;
    justify-content: space-between;
    padding-top: 40px;
  }
`
const noChangesWarning = (
  <strong key="jt">{t`You will not be able to make changes once it is submitted`}</strong>
)
const Confirmation = ({ onClose, lessonId }) => {
  const queryClient = useQueryClient()
  const onMakeChanges = useNavigationHandler(`/planning/assignments/detail/${lessonId}`)
  const { mutate: submitLesson, isLoading } = useMutation(
    () => api.fetch(`/plans/${lessonId}/complete`, { method: 'POST', version: 'v1' }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('userMenu')
        onClose()
      }
    }
  )

  return (
    <Modal isOpen={true} onClose={onClose} hasBorders={false}>
      <Modal.Header>
        <Header>
          <QuestionCircleIcon size="large" />
          <Heading mb="0" ml="15px" level="2">
            {t`Confirmation`}
          </Heading>
        </Header>
      </Modal.Header>
      <Modal.Body>
        <Text size="large">
          {jt`Are you sure you want to submit this lesson? When you do the person who assigned it to you can review it. ${noChangesWarning}.`}
        </Text>
      </Modal.Body>
      <Modal.Footer>
        <FlexFooter direction="column">
          <CancelButton
            size="large"
            variant="outlined"
            display="monochrome"
            onClick={onClose}>
            {t`Cancel`}
          </CancelButton>
          <ButtonBox>
            <Button size="large" variant="outlined" onClick={onMakeChanges}>
              {t`Make Changes`}
            </Button>
            <Button
              size="large"
              isLoading={isLoading}
              isDisabled={isLoading}
              onClick={submitLesson}>
              {t`Confirm and Submit`}
            </Button>
          </ButtonBox>
        </FlexFooter>
      </Modal.Footer>
    </Modal>
  )
}

Confirmation.propTypes = {
  lessonId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired
}

export { Confirmation }
